import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePpjkEvent } from '@/features/realtime/ppjkEvents';
import ColleagueFilledPanel from './ColleagueFilledPanel';
import { createOwnWriteGuard } from '@/features/consignee-import/ownWriteGuard';
import { Button, Modal, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconCheck } from '@tabler/icons-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import { JarvisPrimaryButton } from '@components/jarvis-ui';
import { useSidebarIdentity, useSidebarMenus, useSidebarSurface } from '@components/sidebar/sidebar-v2';
import { MODULE_BY_SIDEBAR_LABEL } from '@constants/modules';
import type { ImportResult } from '@/features/consignee-import/types';
import { useUpdateOnboarding } from './api';
import { useMyStatementConfigs } from '@/features/statement-configs/api';
import { statementConfigKeys } from '@/features/statement-configs/keys';
import { resolveFirstLoginCopy } from './i18n';
import StepConsignee from './steps/StepConsignee';
import StepFilm from './steps/StepFilm';
import StepPernyataan, { type StepPernyataanHandle } from './steps/StepPernyataan';
import StepTampilan from './steps/StepTampilan';
import {
  colleagueConsigneePanelVisible,
  colleagueStatementPanelVisible,
  dataStepsShownIn,
  filmCopyFor,
  filmVariantFor,
  nextStepAfter,
  pickUtamaStatement,
  railNoteFor,
  resolveAfterRefresh,
  STEP_ORDER,
  stepsFor,
  type OnboardingState,
  type OnboardingStepKey,
} from './types';

import { ONBOARDING_MODAL_Z_INDEX } from './constants';

const BRAND_LOGO = '/image/png/apex-logo.png';
const BRAND_LOGO_WHITE = '/image/png/apex-logo-white.png';

export interface OnboardingWizardProps {
  opened: boolean;
  state: OnboardingState;
  /** Re-reads the server state; used before a data step renders so a colleague's save is honoured. */
  refresh: () => Promise<OnboardingState | undefined>;
  /** "Lanjutkan nanti": the wizard closes and will not auto-open again. */
  onLater: () => void;
  /** Every visible step handled. */
  onComplete: () => void;
  /** False for a help-menu replay by a non-pending user: no onboarding PUTs at all. */
  recordProgress?: boolean;
  zIndex?: number;
}

/**
 * The first-login wizard. Owns which step is on screen and the per-step
 * server bookkeeping; the steps own their forms. The left rail paints with the
 * sidebar's own surface so the wizard reads as part of the app, not a
 * marketing overlay.
 */
export const OnboardingWizard = ({
  opened,
  state: initialState,
  refresh,
  onLater,
  onComplete,
  recordProgress = true,
  zIndex = ONBOARDING_MODAL_Z_INDEX,
}: OnboardingWizardProps) => {
  const { colors: themeColors } = useTheme();
  const { language } = useLanguage();
  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => resolveFirstLoginCopy(language, key, vars),
    [language]
  );
  const { sidebarStyle, isDarkSidebar } = useSidebarSurface();
  const { userEmail, professionalModeEnabled } = useSidebarIdentity();
  const { filteredMenus, isFeatureEnabled } = useSidebarMenus({ userEmail, professionalModeEnabled });
  // In the menu AND not module-locked: a locked Master row stays visible but
  // dimmed, and neither data page under /master would open (see StepContext).
  // The film only needs the Master group; each data step needs its own page.
  // A locked Master row stays visible but dimmed, and neither page would open.
  const reachability = useMemo(() => {
    const masterGroup = filteredMenus.find((item) => item.label === 'Master');
    const moduleCode = MODULE_BY_SIDEBAR_LABEL.Master;
    const enabled = moduleCode ? isFeatureEnabled(moduleCode) : true;
    const group = Boolean(masterGroup) && enabled;
    // subLinks are already filtered by role menus and the Master allowlist.
    const hasPage = (link: string) => group && Boolean(masterGroup?.subLinks?.some((sub) => sub.link === link));
    return {
      master: group,
      statement: hasPage('/master/statement'),
      consignee: hasPage('/master/consignee'),
    };
  }, [filteredMenus, isFeatureEnabled]);
  const masterReachable = reachability.master;
  const narrow = useMediaQuery('(max-width: 767px)');
  const update = useUpdateOnboarding();

  // The gate mounts a fresh wizard per run, so initial values are the run's values.
  const [state, setState] = useState(initialState);
  // Decided at open (the gate waits for company features and permissions
  // first) so the step list and the film cannot disagree later in the run.
  const [reachableAtOpen] = useState(reachability);
  const [filmVariant] = useState(() => filmVariantFor(initialState, masterReachable));
  const [currentKey, setCurrentKey] = useState<OnboardingStepKey>('tampilan');
  // Steps the user has actually seen this run. A data step drops out of
  // `stepsFor` the moment its data exists (that is how a colleague's save
  // removes it), but a step the user just completed must stay on the rail
  // as done rather than vanish.
  const [shownKeys, setShownKeys] = useState<OnboardingStepKey[]>(['tampilan']);
  // Steps already recorded as done this run. Revisiting one via Kembali must
  // not turn it into 'skipped' and must not re-record it unchanged.
  const [doneKeys, setDoneKeys] = useState<OnboardingStepKey[]>([]);
  // Reported by the Pernyataan form; decides "Lanjut" vs "Simpan & lanjut" on a revisit.
  const [pernyataanDirty, setPernyataanDirty] = useState(true);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const [busy, setBusy] = useState(false);
  // Realtime (contract §8): a colleague may fill a data step while this user
  // is in the wizard. These track what this user did, so the notice appears
  // only for someone else's change.
  const [statementSavedThisRun, setStatementSavedThisRun] = useState(false);
  const [statementPanelDismissed, setStatementPanelDismissed] = useState(false);
  const [consigneePanelDismissed, setConsigneePanelDismissed] = useState(false);
  // This user's own import: in flight, and the echo window after it succeeds.
  const ownImport = useRef(createOwnWriteGuard()).current;
  // Same for this user's Pernyataan saves (the step's own save, "Timpa" included).
  const ownStatement = useRef(createOwnWriteGuard()).current;
  // The step's 409 panel is open: the colleague notice would say the same twice.
  const [statementConflictOpen, setStatementConflictOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const queryClient = useQueryClient();
  const [importedCount, setImportedCount] = useState<number | null>(null);
  const pernyataanRef = useRef<StepPernyataanHandle>(null);

  const context = useMemo(
    () => ({ statementReachable: reachableAtOpen.statement, consigneeReachable: reachableAtOpen.consignee }),
    [reachableAtOpen]
  );
  const steps = useMemo(() => stepsFor(state, context), [state, context]);
  const railSteps = useMemo(
    () => STEP_ORDER.filter((key) => steps.includes(key) || shownKeys.includes(key)),
    [steps, shownKeys]
  );
  const currentIndex = Math.max(0, railSteps.indexOf(currentKey));
  const filmCopy = filmCopyFor(filmVariant, dataStepsShownIn(railSteps), state);

  const record = useCallback(
    async (key: OnboardingStepKey, result: 'done' | 'skipped') => {
      // A step recorded as done stays done: nothing to send again.
      if (doneKeys.includes(key)) return;
      if (!recordProgress) {
        // Replay: the screen behaves the same, the server hears nothing.
        if (result === 'done') setDoneKeys((previous) => [...previous, key]);
        return;
      }
      try {
        await update.mutateAsync({ steps: { [key]: result } });
        if (result === 'done') setDoneKeys((previous) => [...previous, key]);
      } catch (error) {
        // Bookkeeping only; the user's choice already took effect on screen.
        console.warn('[first-login] step record failed', error);
      }
    },
    [update, doneKeys, recordProgress]
  );

  /**
   * Moves past `from`, honouring anything a colleague filled in the meantime.
   * A refetch that did not genuinely answer (flagged `unavailable`, or a
   * thrown error) leaves the current state and step list untouched: a blip
   * must never jump the user past steps they have not seen.
   */
  const advance = useCallback(
    async (from: OnboardingStepKey) => {
      let latest = state;
      try {
        latest = resolveAfterRefresh(state, await refresh());
      } catch (error) {
        console.warn('[first-login] refresh failed; continuing with the last known state', error);
      }
      if (latest !== state) setState(latest);
      const next = nextStepAfter(from, latest, context);
      if (!next) {
        onComplete();
        return;
      }
      setShownKeys((previous) => (previous.includes(next) ? previous : [...previous, next]));
      setCurrentKey(next);
    },
    [state, refresh, context, onComplete]
  );

  const handleSkip = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await record(currentKey, 'skipped');
      await advance(currentKey);
    } finally {
      setBusy(false);
    }
  };

  const handlePrimary = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (currentKey === 'pernyataan') {
        // Already saved and untouched since: nothing to send.
        const untouched = doneKeys.includes('pernyataan') && !pernyataanDirty;
        if (!untouched) {
          const saved = await pernyataanRef.current?.save();
          if (!saved) return;
          setStatementSavedThisRun(true);
        }
      }
      await record(currentKey, 'done');
      if (currentKey === 'film') {
        onComplete();
        return;
      }
      await advance(currentKey);
    } finally {
      setBusy(false);
    }
  };

  /** Back to the previous step on the rail. Records nothing; forms keep their values. */
  const handleBack = () => {
    if (busy) return;
    const previous = railSteps[railSteps.indexOf(currentKey) - 1];
    if (!previous) return;
    setCurrentKey(previous);
    // Kembali unmounts itself on the first step; keep focus in the footer.
    window.requestAnimationFrame(() => primaryRef.current?.focus());
  };

  /** Re-reads the state for a realtime event; a failed read changes nothing. */
  const refreshFromEvent = useCallback(async () => {
    try {
      const fresh = await refresh();
      setState((previous) => resolveAfterRefresh(previous, fresh));
    } catch (error) {
      console.warn('[first-login] refresh after realtime event failed', error);
    }
  }, [refresh]);

  usePpjkEvent('statement_config_changed', () => {
    void queryClient.invalidateQueries({ queryKey: statementConfigKeys.all });
    void refreshFromEvent();
    // Our own save's broadcast is not a colleague's change.
    if (ownStatement.isSuppressed()) return;
    setStatementPanelDismissed(false);
  });

  const handleStatementSaveState = (saveState: 'start' | 'success' | 'error') => {
    if (saveState === 'start') ownStatement.begin();
    else if (saveState === 'success') ownStatement.succeed();
    else ownStatement.fail();
  };
  usePpjkEvent('consignee_master_changed', () => {
    // Our own import's broadcast is not a colleague's change.
    if (ownImport.isSuppressed()) return;
    setConsigneePanelDismissed(false);
    void refreshFromEvent();
  });

  const handleApplyStateChange = (applyState: 'start' | 'success' | 'error') => {
    if (applyState === 'start') {
      ownImport.begin();
      setImporting(true);
      return;
    }
    if (applyState === 'success') ownImport.succeed();
    else ownImport.fail();
    setImporting(false);
  };

  /** "Lanjut" on a colleague panel: the step counts as done and the wizard moves on. */
  const handleColleagueContinue = async (step: OnboardingStepKey) => {
    if (busy) return;
    setBusy(true);
    try {
      await record(step, 'done');
      await advance(step);
    } finally {
      setBusy(false);
    }
  };

  const handleImported = (result: ImportResult) => {
    setImportedCount(result.imported.new + result.imported.updated);
  };

  const showStatementPanel =
    shownKeys.includes('pernyataan') &&
    !statementConflictOpen &&
    colleagueStatementPanelVisible({
      hasStatement: state.hasStatement,
      savedThisRun: statementSavedThisRun,
      dismissed: statementPanelDismissed,
      saving: busy && currentKey === 'pernyataan',
    });
  const showConsigneePanel =
    shownKeys.includes('consignee') &&
    colleagueConsigneePanelVisible({
      consigneeCount: state.consigneeCount,
      importedThisRun: importedCount !== null,
      dismissed: consigneePanelDismissed,
      importing,
    });
  const colleagueStatement = pickUtamaStatement(useMyStatementConfigs(showStatementPanel).data);

  const currentDone = doneKeys.includes(currentKey);
  const primaryLabel =
    currentKey === 'film'
      ? t('footer.finish')
      : currentKey === 'consignee'
        ? t('footer.next')
        : currentKey === 'tampilan'
          ? t('footer.next')
          : currentDone && !pernyataanDirty
            ? t('footer.next')
            : t('footer.saveNext');
  const primaryDisabled = currentKey === 'consignee' && importedCount === null;
  // A step already recorded as done offers no Lewati: skipping it would
  // overwrite 'done' with 'skipped' while its data exists.
  const showSkip = currentKey !== 'film' && !currentDone;
  const showBack = railSteps.indexOf(currentKey) > 0;

  const railNote =
    currentKey === 'consignee'
      ? t('rail.note.consignee')
      : currentKey === 'pernyataan'
        ? t('rail.note.private')
        : t(`rail.note.${railNoteFor(filmCopy)}`);

  const railTextColor = isDarkSidebar ? 'rgba(255,255,255,0.85)' : themeColors.text;
  const railMuted = isDarkSidebar ? 'rgba(255,255,255,0.55)' : themeColors.textSecondary;
  const accent = themeColors.accent ?? themeColors.primary;

  const stepList = (
    <div style={{ display: 'flex', flexDirection: narrow ? 'row' : 'column', gap: narrow ? 14 : 4, flexWrap: 'wrap' }}>
      {railSteps.map((key, index) => {
        const isCurrent = key === currentKey;
        const isDone = index < currentIndex;
        return (
          <div
            key={key}
            aria-current={isCurrent ? 'step' : undefined}
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
              padding: narrow ? 0 : '8px 0',
              opacity: isCurrent || isDone ? 1 : 0.7,
              color: railTextColor,
            }}
          >
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                border: `1.5px solid ${isCurrent ? accent : isDone ? railTextColor : railMuted}`,
                backgroundColor: isCurrent ? accent : isDone ? railTextColor : 'transparent',
                color: isCurrent ? themeColors.buttonText ?? '#FFFFFF' : isDone ? themeColors.sidebar : railMuted,
                display: 'grid',
                placeItems: 'center',
                fontSize: 11,
                fontWeight: 600,
                flex: 'none',
                marginTop: 1,
              }}
            >
              {isDone ? <IconCheck size={12} stroke={2.5} /> : index}
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: isCurrent ? 600 : 500, lineHeight: 1.3 }}>
                {t(key === 'film' ? `step.film.${filmCopy}.title` : `step.${key}.title`)}
              </div>
              {!narrow && <div style={{ fontSize: 12, color: railMuted }}>{t(`step.${key}.sub`)}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <Modal
      opened={opened}
      onClose={() => {}}
      fullScreen
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      trapFocus
      lockScroll
      zIndex={zIndex}
      padding={0}
      transitionProps={{ transition: 'fade', duration: 200 }}
      styles={{
        // Mantine's full-screen content is a 100dvh scroll container. The
        // wizard scrolls its own step body instead, so the content must clip
        // and the body must be allowed to shrink (min-height 0) for the
        // footer to stay on screen with the last row reachable above it.
        content: {
          backgroundColor: themeColors.background,
          backgroundImage: themeColors.backgroundGradient,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
        body: {
          padding: 0,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: narrow ? 'column' : 'row',
          overflow: 'hidden',
        },
      }}
      aria-label={t('rail.welcome')}
    >
      <aside
        className="jv-glass-surface"
        style={{
          ...sidebarStyle,
          width: narrow ? '100%' : 300,
          minWidth: narrow ? undefined : 300,
          padding: narrow ? '16px 20px' : '26px 24px',
          display: 'flex',
          flexDirection: 'column',
          flex: 'none',
          overflowY: 'auto',
          gap: narrow ? 12 : 18,
          borderRight: narrow ? 'none' : sidebarStyle.borderRight,
          borderBottom: narrow ? `1px solid ${themeColors.border}` : 'none',
        }}
      >
        <img
          src={isDarkSidebar ? BRAND_LOGO_WHITE : BRAND_LOGO}
          alt="APEX Intelligence"
          style={{ height: 28, width: 'auto', alignSelf: 'flex-start', objectFit: 'contain' }}
        />
        <div>
          <Text fw={700} style={{ color: railTextColor, fontSize: 20, lineHeight: 1.2 }}>
            {t('rail.welcome')}
          </Text>
          <Text size="xs" style={{ color: railMuted, marginTop: 2 }}>
            {t('footer.stepOf', { current: currentIndex + 1, total: railSteps.length })}
          </Text>
        </div>
        {stepList}
        {!narrow && (
          <Text size="xs" style={{ color: railMuted, marginTop: 'auto', lineHeight: 1.45 }}>
            {railNote}
          </Text>
        )}
      </aside>

      <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div
          data-onboarding-body
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            // No bottom padding: a sticky bar inside pins to the scrollport's
            // padding box, so padding here would float it above the visible
            // edge. The trailing spacer below gives the last row its room.
            padding: narrow ? '20px 16px 0' : '32px 40px 0',
          }}
        >
          <div style={{ maxWidth: 880, margin: '0 auto' }}>
            {currentKey === 'tampilan' && <StepTampilan />}
            {/* The form steps stay mounted once visited so Kembali brings back
                what was typed or previewed; the film restarts on every visit. */}
            {shownKeys.includes('pernyataan') && (
              <div hidden={currentKey !== 'pernyataan'}>
                {showStatementPanel && (
                  <ColleagueFilledPanel
                    title={t('colleague.statement.title')}
                    details={
                      colleagueStatement
                        ? [
                            { label: t('pernyataan.field.place'), value: colleagueStatement.place },
                            { label: t('pernyataan.field.name'), value: colleagueStatement.name },
                            { label: t('pernyataan.field.position'), value: colleagueStatement.position },
                          ]
                        : undefined
                    }
                    continueLabel={t('colleague.continue')}
                    keepLabel={t('colleague.statement.keep')}
                    onContinue={() => void handleColleagueContinue('pernyataan')}
                    onKeep={() => {
                      // "Tetap ubah": edit on top of the colleague's Utama, so
                      // the save expects their updatedAt (a 409 otherwise).
                      if (colleagueStatement) pernyataanRef.current?.adoptCurrent(colleagueStatement);
                      setStatementPanelDismissed(true);
                    }}
                    busy={busy}
                  />
                )}
                <StepPernyataan
                  ref={pernyataanRef}
                  onDirtyChange={setPernyataanDirty}
                  onSaveStateChange={handleStatementSaveState}
                  onSaved={() => setStatementSavedThisRun(true)}
                  onConflictChange={setStatementConflictOpen}
                />
              </div>
            )}
            {shownKeys.includes('consignee') && (
              <div hidden={currentKey !== 'consignee'}>
                {showConsigneePanel && (
                  <ColleagueFilledPanel
                    title={t('colleague.consignee.title', { n: state.consigneeCount })}
                    continueLabel={t('colleague.continue')}
                    keepLabel={t('colleague.consignee.keep')}
                    onContinue={() => void handleColleagueContinue('consignee')}
                    onKeep={() => setConsigneePanelDismissed(true)}
                    busy={busy}
                  />
                )}
                <StepConsignee
                  onImported={handleImported}
                  importedCount={importedCount}
                  onApplyStateChange={handleApplyStateChange}
                />
              </div>
            )}
            {currentKey === 'film' && <StepFilm variant={filmVariant} copy={filmCopy} />}
            <div style={{ height: narrow ? 40 : 56 }} aria-hidden="true" />
          </div>
        </div>
        <div
          data-onboarding-footer
          style={{
            flex: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            padding: narrow ? '12px 16px' : '14px 40px',
            borderTop: `1px solid ${themeColors.border}`,
            backgroundColor: themeColors.card,
          }}
        >
          <Button
            variant="subtle"
            color="gray"
            onClick={onLater}
            disabled={busy}
            style={{ color: themeColors.textSecondary }}
          >
            {t('footer.later')}
          </Button>
          <div style={{ display: 'flex', gap: 8 }}>
            {showBack && (
              <Button
                variant="subtle"
                color="gray"
                onClick={handleBack}
                disabled={busy}
                style={{ color: themeColors.textSecondary }}
              >
                {t('footer.back')}
              </Button>
            )}
            {showSkip && (
              <Button
                variant="outline"
                onClick={handleSkip}
                disabled={busy}
                styles={{ root: { borderColor: themeColors.border, color: themeColors.text } }}
              >
                {t('footer.skip')}
              </Button>
            )}
            <JarvisPrimaryButton
              ref={primaryRef}
              onClick={handlePrimary}
              disabled={primaryDisabled || busy}
              loading={busy}
            >
              {primaryLabel}
            </JarvisPrimaryButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default OnboardingWizard;
